import React, { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { hereOf, loginPath } from "@/lib/loginPath";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** "List my shop" — City Home v2's owner-acquisition card (design_handoff_website_v2 §4) links
 * here. `POST /onboarding-requests` already exists and is already called by the mobile app
 * (FEATURES.md §31); the website previously had neither this page nor a nav entry to it at all
 * (§43's own explicit note) — this closes that mobile/website parity gap with the smallest
 * possible page, not a new backend capability. */
export default function OnboardingRequest() {
  const { t, lang, tenant, auth } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  // `?kind=worker` reuses this page for the "get me listed as a worker" case. The backend has
  // always accepted both (ONBOARDING_KINDS = business, worker); only this page hardcoded one.
  const [search] = useSearchParams();
  const kind = search.get("kind") === "worker" ? "worker" : "business";
  const [form, setForm] = useState({
    // The person's own name belongs in the contact fields, not the BUSINESS-name field — prefilling
    // it there made owners submit their own name as their shop's name (UAT L12). A worker's
    // listing IS under their own name, so that prefill stays.
    name: kind === "worker" ? auth?.identity?.display_name || "" : "",
    phone: auth?.identity?.phone_e164 || "",
    trade_or_category: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding-requests", { kind, ...form });
      setSubmitted(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-lg px-5 py-20 text-center" data-testid="onboarding-request-success">
        <h1 className="font-heading text-2xl font-semibold">{t("onboardingRequest.thanksTitle")}</h1>
        <p className="mt-3 text-muted-foreground">{t("onboardingRequest.thanksBody")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/")}>
          {t("landmark.backToCity")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-5 py-14 sm:px-8" data-testid="onboarding-request-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">{t("onboardingRequest.title", { city: localized(lang, tenant, "name") })}</h1>
      <p className="mt-2 text-muted-foreground">{t("onboardingRequest.subtitle")}</p>

      {!auth ? (
        <Button variant="outline" className="mt-6 rounded-full" data-testid="onboarding-request-signin-btn" onClick={() => navigate(loginPath(hereOf(location)))}>
          {t("common.signIn")}
        </Button>
      ) : (
        <div className="mt-6 space-y-4 rounded-2xl border border-border p-6">
          <div className="space-y-1.5">
            <Label htmlFor="req-name">
              {kind === "business" ? t("onboardingRequest.businessNameLabel") : t("operator.fields.name")}
            </Label>
            <Input id="req-name" data-testid="onboarding-request-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="req-phone">{t("operator.fields.phone")}</Label>
            <Input id="req-phone" data-testid="onboarding-request-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="req-category">{t("onboardingRequest.categoryLabel")}</Label>
            <Input id="req-category" data-testid="onboarding-request-category" value={form.trade_or_category} onChange={(e) => setForm({ ...form, trade_or_category: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="req-note">{t("onboardingRequest.noteLabel")}</Label>
            <Textarea id="req-note" rows={3} data-testid="onboarding-request-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <Button className="rounded-full" disabled={busy || !form.name || !form.phone} data-testid="onboarding-request-submit" onClick={submit}>
            {busy ? t("common.saving") : t("common.submit")}
          </Button>
        </div>
      )}
    </div>
  );
}
